/**
 * Tax groups — `/countries/:cc/admin/tax-groups`. Design U5 (migration 30, OD-1).
 *
 * AUTHORED (no Figma exists for the admin surface); assembled from existing
 * primitives only, in the `NetworkRulesPage` idiom: a stating alert, a create
 * card, the table, and inline dialogs for the two actions that change what every
 * FUTURE quote in the country does.
 *
 * ===========================================================================
 * WHAT THIS SCREEN SAYS OUT LOUD, BECAUSE THE MODEL IS NEW
 * ===========================================================================
 * A COUNTRY'S TAX CONFIGURATION IS NAMED, CONCURRENT GROUPS (OD-1 repealed D1's
 * one-rate-per-country-per-date for groups). The DEFAULT is what a bare quote
 * create resolves; the picker in the builder offers the rest. Retiring a group
 * removes it from the PICKER ONLY — documents that already carry it keep
 * re-pricing (M6) — which is why "Retire" is not "Delete" and why the sitting
 * default refuses to retire until a successor is named (M6c).
 *
 * THE RATE-CHANGE FLOW IS THE D1 PROCEDURE, per (branch, name) family: close the
 * incumbent line (give it an end date), open the successor. The server's overlap
 * 409 is the guard; this screen just makes the two steps visible.
 *
 * ===========================================================================
 * D5, APPLIED
 * ===========================================================================
 * Permission gates are ABSENT (no `config.manage` → `NoAdminAccess`, like every
 * admin screen). STATE gates are DISABLED WITH THE REASON BESIDE THEM: Retire on
 * the default is rendered disabled with the successor rule as its caption,
 * because "where did Retire go" is worse than reading one sentence.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Percent } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  SelectField,
  SkeletonRows,
  TextField,
} from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { COUNTRIES } from '../../domain/countries';
import { ApiRequestError } from '../../lib/api';
import {
  addTaxGroupLine,
  createTaxGroup,
  deleteTaxGroup,
  fetchAdminTaxGroups,
  fetchTaxGroupDetail,
  patchTaxGroup,
  patchTaxGroupLine,
  taxGroupAdminKeys,
} from './tax-groups-api';
import type { AdminTaxGroupDto, AdminTaxGroupLineDto } from './tax-groups-api';
import { ConfirmDialog } from './ConfirmDialog';
import { NoAdminAccess, errorSentence } from './states';

/** The API's own sentence when it carries one — the NetworkRules pattern. */
function apiSentence(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) return error.message;
  return errorSentence(error, fallback);
}

export function TaxGroupsPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();

  const permitted = isGroupAdmin || can(countryCode, 'config.manage');

  const [name, setName] = useState('');
  const [splitMode, setSplitMode] = useState('NONE');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<AdminTaxGroupDto | null>(null);
  const [retiring, setRetiring] = useState<AdminTaxGroupDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const groups = useQuery({
    queryKey: taxGroupAdminKeys.list(countryCode),
    queryFn: ({ signal }) => fetchAdminTaxGroups(countryCode, signal),
    enabled: permitted,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin-tax-groups', countryCode] });
    // The builder's picker reads the same table through its own key.
    void queryClient.invalidateQueries({ queryKey: ['tax-groups', countryCode] });
  };

  const create = useMutation({
    // A STAGED group: the name and mode first, the dated lines in the editor below.
    // (The API accepts lines on create too; the two-step keeps this form honest —
    // a rate change is a LINE operation, and the editor is where lines live.)
    mutationFn: () => createTaxGroup(countryCode, { name: name.trim(), split_mode: splitMode, lines: [] }),
    onSuccess: (detail) => {
      setName('');
      setSplitMode('NONE');
      setActionError(null);
      setSelectedId(detail.id);
      invalidate();
    },
    onError: (error) => setActionError(apiSentence(error, 'The tax group was not created.')),
  });

  const patch = useMutation({
    mutationFn: (input: { id: string; body: { name?: string; active?: boolean; is_default?: boolean } }) =>
      patchTaxGroup(countryCode, input.id, input.body),
    onSuccess: () => {
      setPromoting(null);
      setRetiring(null);
      setActionError(null);
      invalidate();
    },
    onError: (error) => {
      setPromoting(null);
      setRetiring(null);
      setActionError(apiSentence(error, 'The change was not saved.'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteTaxGroup(countryCode, id),
    onSuccess: (result) => {
      setActionError(null);
      if (selectedId === result.id) setSelectedId(null);
      invalidate();
    },
    onError: (error) =>
      setActionError(apiSentence(error, 'The group was not deleted. Retire it instead?')),
  });

  if (!permitted) return <NoAdminAccess />;

  const rows = groups.data ?? [];
  const countryName = COUNTRIES[countryCode].name;
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Tax groups"
        subtitle={`The named tax configurations ${countryName} quotes price under. The default prices every quote that does not pick one; Tally's invoice remains the tax truth.`}
      />

      <div className="flex flex-col gap-4 px-6">
        {actionError === null ? null : <Alert tone="danger">{actionError}</Alert>}

        <Card title="Add a tax group">
          <form
            className="flex flex-wrap items-start gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim() === '' || create.isPending) return;
              create.mutate();
            }}
          >
            <div className="w-full max-w-sm">
              <TextField
                label="Name"
                value={name}
                maxLength={80}
                placeholder="e.g. Export – LUT"
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="w-full max-w-xs">
              <SelectField
                label="Mode"
                id="tax-group-mode"
                options={[
                  { value: 'NONE', label: 'Plain — every line always applies' },
                  { value: 'IN_GST_STATE_SPLIT', label: 'India GST state split' },
                ]}
                value={splitMode}
                onChange={setSplitMode}
              />
            </div>
            {/* mt-[26px] aligns with the field boxes — the NetworkRules measurement. */}
            <Button type="submit" className="mt-[26px]" disabled={name.trim() === '' || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add group'}
            </Button>
          </form>
          <p className="text-label text-fg-secondary">
            The group is created empty — add its dated rate lines below. The mode is fixed at
            creation: re-moding a group documents already reference would change what their
            re-prices mean.
          </p>
        </Card>

        <Card title={`Tax groups in ${countryName}`}>
          {groups.isPending ? (
            <SkeletonRows rows={3} label="Loading tax groups" />
          ) : groups.isError ? (
            <EmptyState
              title="Tax groups could not be loaded"
              description={errorSentence(groups.error, 'The server did not answer.')}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Percent aria-hidden="true" className="size-5" />}
              title="No tax groups"
              description="Add one above. Until a default exists, quotes cannot be priced in this country."
            />
          ) : (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-line text-th uppercase text-fg-muted">
                  <th scope="col" className="py-2 pr-3 font-normal">Name</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Mode</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Status</th>
                  <th scope="col" className="py-2 text-right font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <GroupRow
                    key={row.id}
                    row={row}
                    selected={row.id === selectedId}
                    onOpen={() => {
                      setActionError(null);
                      setSelectedId(row.id === selectedId ? null : row.id);
                    }}
                    onPromote={() => {
                      setActionError(null);
                      setPromoting(row);
                    }}
                    onRetire={() => {
                      setActionError(null);
                      setRetiring(row);
                    }}
                    onReactivate={() => {
                      setActionError(null);
                      patch.mutate({ id: row.id, body: { active: true } });
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {selected === null ? null : (
          <LineEditorCard
            key={selected.id}
            countryCode={countryCode}
            group={selected}
            onDeleteEmpty={() => remove.mutate(selected.id)}
            deleting={remove.isPending}
          />
        )}
      </div>

      <ConfirmDialog
        open={promoting !== null}
        title={`Make '${promoting?.name ?? ''}' the ${countryName} default?`}
        body={
          'Every quote that does not pick a group prices under the default from the moment ' +
          'this saves. The current default keeps existing and stays pickable — only the ' +
          'fallback moves. Existing documents are not re-priced.'
        }
        confirmLabel="Set default"
        busy={patch.isPending}
        onConfirm={() => {
          if (promoting !== null) patch.mutate({ id: promoting.id, body: { is_default: true } });
        }}
        onCancel={() => setPromoting(null)}
      />

      <ConfirmDialog
        open={retiring !== null}
        title={`Retire '${retiring?.name ?? ''}'?`}
        body={
          'The group leaves the quote builder picker immediately. Documents that already ' +
          'carry it keep re-pricing under it (M6), and it stays listed here as the audit ' +
          'record — retirement can be undone with Reactivate.'
        }
        confirmLabel="Retire group"
        busy={patch.isPending}
        onConfirm={() => {
          if (retiring !== null) patch.mutate({ id: retiring.id, body: { active: false } });
        }}
        onCancel={() => setRetiring(null)}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── table row ── */

const SPLIT_MODE_LABELS: Record<string, string> = {
  NONE: 'Plain',
  IN_GST_STATE_SPLIT: 'GST state split',
};

function GroupRow({
  row,
  selected,
  onOpen,
  onPromote,
  onRetire,
  onReactivate,
}: {
  row: AdminTaxGroupDto;
  selected: boolean;
  onOpen: () => void;
  onPromote: () => void;
  onRetire: () => void;
  onReactivate: () => void;
}): React.JSX.Element {
  return (
    <tr className="crf-register-row border-b border-line last:border-b-0">
      <td className={`py-2.5 pr-3 text-body ${row.active ? 'text-fg' : 'text-fg-secondary'}`}>
        {row.name}
      </td>
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">
        {SPLIT_MODE_LABELS[row.split_mode] ?? row.split_mode}
      </td>
      <td className="py-2.5 pr-3">
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {row.is_default ? (
            <span className="inline-flex items-center rounded-pill bg-ok-bg px-2 py-0.5 text-label text-ok-fg">
              Default
            </span>
          ) : null}
          {row.active ? null : (
            /* Bordered, unfilled — the agent-keys/network-rules AA measurement. */
            <span className="inline-flex items-center rounded-pill border border-fg-muted px-2 py-0.5 text-label text-fg-secondary">
              Retired
            </span>
          )}
        </span>
      </td>
      <td className="py-2.5 text-right">
        <span className="inline-flex items-center gap-1">
          <Button variant="quiet" onClick={onOpen}>
            {selected ? 'Close lines' : 'Edit lines'}
          </Button>
          {row.is_default || !row.active ? null : (
            <Button variant="quiet" onClick={onPromote}>
              Set default…
            </Button>
          )}
          {row.active ? (
            row.is_default ? (
              /*
               * A STATE gate, not a permission gate — DISABLED with the reason (D5).
               * The server refuses this with DEFAULT_NEEDS_SUCCESSOR; saying it here
               * saves the round trip and teaches the rule.
               */
              <Button
                variant="quiet"
                disabled
                title="The default cannot be retired — make another group the default first (M6)."
              >
                Retire…
              </Button>
            ) : (
              <Button variant="quiet" onClick={onRetire}>
                Retire…
              </Button>
            )
          ) : (
            <Button variant="quiet" onClick={onReactivate}>
              Reactivate
            </Button>
          )}
        </span>
      </td>
    </tr>
  );
}

/* ───────────────────────────────────────────────────────────── line editor ── */

const APPLIES_LABELS: Record<string, string> = {
  ALWAYS: 'Always',
  INTRA_STATE: 'Intra-state',
  INTER_STATE: 'Inter-state',
};

function LineEditorCard({
  countryCode,
  group,
  onDeleteEmpty,
  deleting,
}: {
  countryCode: ReturnType<typeof useCountryCode>;
  group: AdminTaxGroupDto;
  onDeleteEmpty: () => void;
  deleting: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [lineError, setLineError] = useState<string | null>(null);
  const [applies, setApplies] = useState('ALWAYS');
  const [taxName, setTaxName] = useState('');
  const [rate, setRate] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const detail = useQuery({
    queryKey: taxGroupAdminKeys.detail(countryCode, group.id),
    queryFn: ({ signal }) => fetchTaxGroupDetail(countryCode, group.id, signal),
  });

  const settle = (): void => {
    setLineError(null);
    void queryClient.invalidateQueries({ queryKey: ['admin-tax-groups', countryCode] });
    void queryClient.invalidateQueries({ queryKey: ['tax-groups', countryCode] });
  };

  const add = useMutation({
    mutationFn: () =>
      addTaxGroupLine(countryCode, group.id, {
        ...(applies === 'ALWAYS' ? {} : { applies }),
        tax_name: taxName.trim(),
        rate: rate.trim(),
        effective_from: from,
        ...(to === '' ? {} : { effective_to: to }),
      }),
    onSuccess: () => {
      setTaxName('');
      setRate('');
      setFrom('');
      setTo('');
      settle();
    },
    onError: (error) => setLineError(apiSentence(error, 'The line was not added.')),
  });

  const closeLine = useMutation({
    mutationFn: (input: { lineId: string; effective_to: string }) =>
      patchTaxGroupLine(countryCode, group.id, input.lineId, {
        effective_to: input.effective_to,
      }),
    onSuccess: settle,
    onError: (error) => setLineError(apiSentence(error, 'The line was not closed.')),
  });

  const lines = detail.data?.lines ?? [];

  return (
    <Card title={`Lines on '${group.name}'`}>
      {lineError === null ? null : <Alert tone="danger">{lineError}</Alert>}

      <p className="text-label text-fg-secondary">
        A rate change is two steps, on purpose: close the incumbent line with an end date,
        then open its successor from the next day. Overlapping windows within one
        (branch, name) family are refused — a rate chosen by row order is the defect the
        rule ends.
      </p>

      {detail.isPending ? (
        <SkeletonRows rows={2} label="Loading lines" />
      ) : detail.isError ? (
        <EmptyState
          title="The lines could not be loaded"
          description={errorSentence(detail.error, 'The server did not answer.')}
        />
      ) : lines.length === 0 ? (
        <div className="flex flex-col gap-3">
          <EmptyState
            title="No lines yet"
            description="A group with no active line refuses to price the quotes that pick it — add the first rate below."
          />
          {/* Only a group holding NOTHING may be deleted; this is that moment. */}
          <div>
            <Button
              variant="quiet"
              className="text-danger-fg hover:bg-danger-bg hover:text-danger-fg dark:text-danger-bg dark:hover:text-danger-fg"
              disabled={deleting || group.is_default}
              title={
                group.is_default
                  ? 'The default cannot be deleted — make another group the default first.'
                  : undefined
              }
              onClick={onDeleteEmpty}
            >
              {deleting ? 'Deleting…' : 'Delete this empty group'}
            </Button>
          </div>
        </div>
      ) : (
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-line text-th uppercase text-fg-muted">
              <th scope="col" className="py-2 pr-3 font-normal">Branch</th>
              <th scope="col" className="py-2 pr-3 font-normal">Name</th>
              <th scope="col" className="py-2 pr-3 font-normal">Rate %</th>
              <th scope="col" className="py-2 pr-3 font-normal">From</th>
              <th scope="col" className="py-2 pr-3 font-normal">To</th>
              <th scope="col" className="py-2 text-right font-normal">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                busy={closeLine.isPending}
                onClose={(effectiveTo) =>
                  closeLine.mutate({ lineId: line.id, effective_to: effectiveTo })
                }
              />
            ))}
          </tbody>
        </table>
      )}

      <form
        className="flex flex-wrap items-start gap-3 border-t border-line pt-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (taxName.trim() === '' || rate.trim() === '' || from === '' || add.isPending) return;
          add.mutate();
        }}
      >
        {group.split_mode === 'NONE' ? null : (
          <div className="w-40">
            <SelectField
              label="Branch"
              id={`line-branch-${group.id}`}
              options={[
                { value: 'ALWAYS', label: 'Always' },
                { value: 'INTRA_STATE', label: 'Intra-state' },
                { value: 'INTER_STATE', label: 'Inter-state' },
              ]}
              value={applies}
              onChange={setApplies}
            />
          </div>
        )}
        <div className="w-32">
          <TextField
            label="Tax name"
            value={taxName}
            maxLength={20}
            placeholder="e.g. VAT"
            onChange={(event) => setTaxName(event.target.value)}
          />
        </div>
        <div className="w-28">
          <TextField
            label="Rate %"
            inputMode="decimal"
            value={rate}
            placeholder="e.g. 18"
            onChange={(event) => setRate(event.target.value)}
          />
        </div>
        <div className="w-40">
          <TextField
            label="From"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="w-40">
          <TextField
            label="To (optional)"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <Button
          type="submit"
          className="mt-[26px]"
          disabled={taxName.trim() === '' || rate.trim() === '' || from === '' || add.isPending}
        >
          {add.isPending ? 'Adding…' : 'Add line'}
        </Button>
      </form>
    </Card>
  );
}

function LineRow({
  line,
  busy,
  onClose,
}: {
  line: AdminTaxGroupLineDto;
  busy: boolean;
  onClose: (effectiveTo: string) => void;
}): React.JSX.Element {
  const [closingTo, setClosingTo] = useState('');
  const open = line.effective_to === null;

  return (
    <tr className="crf-register-row border-b border-line last:border-b-0">
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">
        {APPLIES_LABELS[line.applies] ?? line.applies}
      </td>
      <td className="py-2.5 pr-3 text-body text-fg">{line.tax_name}</td>
      {/* A PERCENTAGE, registered non-money — rendered plainly, never computed with. */}
      <td className="tnum py-2.5 pr-3 text-body text-fg">{line.rate}</td>
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">{line.effective_from}</td>
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">
        {line.effective_to ?? 'Open-ended'}
      </td>
      <td className="py-2.5 text-right">
        {open ? (
          <span className="inline-flex items-center gap-2">
            <label className="sr-only" htmlFor={`close-${line.id}`}>
              Close {line.tax_name} on
            </label>
            <input
              id={`close-${line.id}`}
              type="date"
              className="h-9 rounded-sm border border-line bg-subtle px-2.5 text-body13 text-fg outline-none"
              value={closingTo}
              onChange={(event) => setClosingTo(event.target.value)}
            />
            <Button
              variant="quiet"
              disabled={closingTo === '' || busy}
              onClick={() => onClose(closingTo)}
            >
              Close line
            </Button>
          </span>
        ) : (
          <span className="text-label text-fg-secondary">Closed</span>
        )}
      </td>
    </tr>
  );
}
